import { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Camera, Upload, Scan, User, Mail, Phone, Building2, Briefcase, MapPin, Globe, Save, RotateCcw, Loader2, Tag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import FileDropzone, { IMAGE_UPLOAD } from './FileDropzone';

const API = process.env.REACT_APP_BACKEND_URL;

const FIELDS = [
  { key: 'name', label: 'Name', icon: User },
  { key: 'email', label: 'Email', icon: Mail },
  { key: 'phone', label: 'Phone', icon: Phone },
  { key: 'company', label: 'Company', icon: Building2 },
  { key: 'job_title', label: 'Job Title', icon: Briefcase },
  { key: 'address', label: 'Address', icon: MapPin },
  { key: 'website', label: 'Website', icon: Globe },
];

const Scanner = () => {
  const [step, setStep] = useState('capture');
  const [imagePreview, setImagePreview] = useState(null);
  const [extractedData, setExtractedData] = useState({});
  const [locationName, setLocationName] = useState('');
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target.result);
    reader.readAsDataURL(file);
    setStep('extracting');

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await axios.post(`${API}/api/scanner/extract`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setExtractedData(res.data);
      setStep('review');
      toast.success('Data extracted successfully');
    } catch (e) {
      toast.error('Failed to extract data. Try a clearer image.');
      setStep('capture');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/api/scanner/save`, {
        ...extractedData,
        location_name: locationName,
      });
      setStep('saved');
      toast.success('Contact saved to Leads!');
    } catch (e) {
      toast.error('Failed to save');
    }
    setSaving(false);
  };

  const reset = () => {
    setStep('capture');
    setImagePreview(null);
    setExtractedData({});
    setLocationName('');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Scanner</h1>
          <p className="text-sm text-[#71717A] mt-1">Scan business cards, flyers, badges to save contacts</p>
        </div>
        {step !== 'capture' && (
          <Button data-testid="scanner-reset" variant="outline" size="sm" onClick={reset}
            className="border-[#27272A] text-[#A1A1AA] hover:text-white">
            <RotateCcw className="w-4 h-4 mr-1" /> New Scan
          </Button>
        )}
      </div>

      {step === 'capture' && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="bg-[#121212] rounded-2xl border border-[#27272A] p-8 flex flex-col items-center gap-6">
          <div className="w-20 h-20 rounded-2xl bg-violet-500/10 flex items-center justify-center">
            <Scan className="w-10 h-10 text-violet-400" />
          </div>
          <div className="text-center">
            <h2 className="text-white font-semibold text-lg">Scan a Document</h2>
            <p className="text-[#71717A] text-sm mt-1">Business card, flyer, brochure, or badge</p>
          </div>
          <FileDropzone
            {...IMAGE_UPLOAD}
            className="w-full max-w-md"
            testId="scanner-file-dropzone"
            label="Choose document image or drag and drop"
            description="JPG, PNG, WebP or HEIC - max 20MB"
            onFilesSelected={(files) => handleFile(files[0])}
          />
          <p className="text-[#525252] text-xs">On mobile, you can also take a new photo.</p>
          <div className="flex gap-3 w-full max-w-xs">
            <Button data-testid="scanner-camera" onClick={() => cameraInputRef.current?.click()}
              className="flex-1 bg-violet-600 hover:bg-violet-700 rounded-xl h-12">
              <Camera className="w-5 h-5 mr-2" /> Camera
            </Button>
            <Button data-testid="scanner-upload" variant="outline" onClick={() => fileInputRef.current?.click()}
              className="flex-1 border-[#27272A] text-[#A1A1AA] hover:text-white rounded-xl h-12">
              <Upload className="w-5 h-5 mr-2" /> Upload
            </Button>
          </div>
          <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])} />
          <input ref={fileInputRef} type="file" accept="image/*" className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])} />
        </motion.div>
      )}

      {step === 'extracting' && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          className="bg-[#121212] rounded-2xl border border-[#27272A] p-8 flex flex-col items-center gap-4">
          {imagePreview && (
            <img src={imagePreview} alt="Scanned" className="w-full max-w-sm rounded-xl border border-[#27272A] mb-2" />
          )}
          <Loader2 className="w-8 h-8 text-violet-400 animate-spin" />
          <p className="text-white font-medium">Extracting contact info...</p>
          <p className="text-[#71717A] text-sm">AI is reading your document</p>
        </motion.div>
      )}

      {step === 'review' && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
          {imagePreview && (
            <div className="bg-[#121212] rounded-2xl border border-[#27272A] p-3">
              <img src={imagePreview} alt="Scanned" className="w-full max-w-sm mx-auto rounded-xl" />
            </div>
          )}
          <div className="bg-[#121212] rounded-2xl border border-[#27272A] p-5 space-y-4">
            <h2 className="text-white font-semibold">Review Extracted Data</h2>
            <p className="text-[#71717A] text-xs">Edit any field before saving to Leads</p>

            {FIELDS.map(f => {
              const Icon = f.icon;
              return (
                <div key={f.key}>
                  <Label className="text-[#A1A1AA] text-xs flex items-center gap-1.5 mb-1">
                    <Icon className="w-3.5 h-3.5" /> {f.label}
                  </Label>
                  <Input data-testid={`scanner-field-${f.key}`}
                    value={extractedData[f.key] || ''}
                    onChange={(e) => setExtractedData(prev => ({ ...prev, [f.key]: e.target.value }))}
                    className="bg-[#1E1E1E] border-[#27272A] text-white"
                    placeholder={`Enter ${f.label.toLowerCase()}`} />
                </div>
              );
            })}

            <div className="pt-2 border-t border-[#27272A]">
              <Label className="text-[#A1A1AA] text-xs flex items-center gap-1.5 mb-1">
                <Tag className="w-3.5 h-3.5" /> Where did you meet this lead?
              </Label>
              <Input data-testid="scanner-field-location"
                value={locationName}
                onChange={(e) => setLocationName(e.target.value)}
                className="bg-[#1E1E1E] border-[#27272A] text-white"
                placeholder="e.g. BNI Meeting, Trade Show, Networking Event" />
            </div>

            <Button data-testid="scanner-save" onClick={handleSave} disabled={saving}
              className="w-full bg-violet-600 hover:bg-violet-700 rounded-xl h-11 mt-2">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save to Leads
            </Button>
          </div>
        </motion.div>
      )}

      {step === 'saved' && (
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
          className="bg-[#121212] rounded-2xl border border-[#27272A] p-8 flex flex-col items-center gap-4 text-center">
          <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center">
            <Save className="w-8 h-8 text-green-400" />
          </div>
          <h2 className="text-white font-semibold text-lg">Contact Saved!</h2>
          <p className="text-[#71717A] text-sm">
            <strong className="text-white">{extractedData.name || 'Contact'}</strong>
            {extractedData.company && <> from <strong className="text-white">{extractedData.company}</strong></>}
            {' '}has been added to your Leads.
          </p>
          <Button data-testid="scanner-scan-another" onClick={reset}
            className="bg-violet-600 hover:bg-violet-700 rounded-xl">
            <Scan className="w-4 h-4 mr-2" /> Scan Another
          </Button>
        </motion.div>
      )}
    </div>
  );
};

export default Scanner;
